// ============================================================================
// SOURCE UPLOADER - Create new source from text or file WITH AI Analysis
// ============================================================================

import { useState, useRef } from 'react'
import { useProjectStore } from '@/stores/projectStore'
import { useSessionStore } from '@/stores/sessionStore'
import type { ContentType, SourceWithStats } from '@/lib/api/client'
import { sourcesApi } from '@/lib/api/client'
import { CONTENT_TYPES, CONTENT_TYPE_ICONS } from '@/lib/analyzer/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { FileUp, Sparkles, Loader2, AlertCircle } from 'lucide-react'

interface SourceUploaderProps {
  onClose: () => void
  onSourceCreated: (source: SourceWithStats) => void
  /** If true, only create source without AI analysis */
  skipAnalysis?: boolean
}

export function SourceUploader({ onClose, onSourceCreated, skipAnalysis = false }: SourceUploaderProps) {
  const { currentProject } = useProjectStore()
  const { accountId, projectId } = useSessionStore()
  const visualAnalysisEnabled = currentProject?.visualAnalysisEnabled ?? false
  const [activeTab, setActiveTab] = useState<'paste' | 'upload'>('paste')
  const [title, setTitle] = useState('')
  const [contentType, setContentType] = useState<ContentType>('custom')
  const [content, setContent] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    setFile(selectedFile)
    setLocalError(null)

    // Auto-set title from filename if empty
    if (!title) {
      setTitle(selectedFile.name.replace(/\.[^.]+$/, ''))
    }

    // Preview content for non-PDF files
    if (!selectedFile.name.toLowerCase().endsWith('.pdf')) {
      try {
        const text = await selectedFile.text()
        setContent(text)
      } catch (err) {
        console.warn('Failed to preview file:', err)
      }
    } else {
      setContent('') // Clear preview for PDFs (extracted during analysis)
    }
  }

  // Handle form submit - just create source, backend auto-triggers analysis
  const handleSubmit = async () => {
    if (!accountId || !projectId) {
      setLocalError('Account or project not selected')
      return
    }

    if (!title.trim()) {
      setLocalError('Title is required')
      return
    }

    const hasContent = activeTab === 'upload' ? !!file : content.trim().length >= 50

    if (!hasContent) {
      setLocalError(activeTab === 'upload' ? 'Please select a file' : 'Content must be at least 50 characters')
      return
    }

    setLocalError(null)
    setIsUploading(true)

    try {
      let rawContent: string
      let markdownContent: string | undefined
      let fileData: string | undefined
      let fileName: string | undefined
      let fileType: 'pdf' | 'md' | 'txt' | undefined

      if (activeTab === 'upload' && file) {
        fileName = file.name
        const isPdf = file.name.toLowerCase().endsWith('.pdf')
        fileType = isPdf ? 'pdf' : file.name.toLowerCase().endsWith('.md') ? 'md' : 'txt'

        if (isPdf) {
          // For PDFs, send the binary data
          const arrayBuffer = await file.arrayBuffer()
          fileData = btoa(
            new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
          )
          // Backend will extract text
          rawContent = '' // Will be extracted on backend
        } else {
          rawContent = await file.text()
        }
        markdownContent = rawContent
      } else {
        rawContent = content
        markdownContent = content
      }

      // Create source - backend will auto-trigger analysis
      const source = await sourcesApi.create(accountId, projectId, {
        title: title.trim(),
        contentType,
        rawContent,
        markdownContent,
        fileName,
        fileType,
        fileData,
      })

      // Convert to SourceWithStats format
      const sourceWithStats: SourceWithStats = {
        ...source,
        facetStats: {
          total: 0,
          pending: 0,
          accepted: 0,
          rejected: 0,
          mapped: 0,
        },
      }

      onSourceCreated(sourceWithStats)
      onClose()
    } catch (error) {
      console.error('Failed to create source:', error)
      setLocalError(error instanceof Error ? error.message : 'Failed to create source')
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Source</DialogTitle>
          <DialogDescription>
            Add content to analyze and extract insights from
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              placeholder="e.g., Customer Interview - John Smith"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isUploading}
            />
          </div>

          {/* Content Type */}
          <div className="space-y-2">
            <Label htmlFor="contentType">Content Type</Label>
            <Select
              value={contentType}
              onValueChange={(v) => setContentType(v as ContentType)}
              disabled={isUploading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select content type" />
              </SelectTrigger>
              <SelectContent>
                {CONTENT_TYPES.map((ct) => {
                  const IconComponent = CONTENT_TYPE_ICONS[ct.icon]
                  return (
                    <SelectItem key={ct.id} value={ct.id}>
                      <span className="flex items-center gap-2">
                        {IconComponent && <IconComponent className="h-4 w-4 text-zinc-500" />}
                        <span>{ct.name}</span>
                      </span>
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              {CONTENT_TYPES.find(ct => ct.id === contentType)?.description}
            </p>
          </div>

          {/* Content Input */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'paste' | 'upload')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="paste" disabled={isUploading}>
                Paste Text
              </TabsTrigger>
              <TabsTrigger value="upload" disabled={isUploading}>
                Upload File
              </TabsTrigger>
            </TabsList>

            <TabsContent value="paste" className="space-y-2">
              <Label htmlFor="content">Content</Label>
              <Textarea
                id="content"
                placeholder="Paste your document content here..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={12}
                className="font-mono text-sm"
                disabled={isUploading}
              />
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                {content.length} characters
                {content.length > 0 && content.length < 50 && (
                  <span className="text-red-500"> (minimum 50)</span>
                )}
              </p>
            </TabsContent>

            <TabsContent value="upload" className="space-y-4">
              <div
                className={`border-2 border-dashed border-zinc-200 dark:border-zinc-700 rounded-lg p-8 text-center transition-colors ${
                  isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-zinc-400 dark:hover:border-zinc-500'
                }`}
                onClick={() => !isUploading && fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.md,.markdown,.pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={isUploading}
                />
                {file ? (
                  <div>
                    <FileUp className="mx-auto mb-2 h-8 w-8 text-solution" />
                    <p className="font-medium">{file.name}</p>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">
                      {(file.size / 1024).toFixed(1)} KB
                      {file.name.toLowerCase().endsWith('.pdf') && visualAnalysisEnabled && (
                        <span className="ml-2 text-solution">
                          <Sparkles className="inline h-3 w-3 mr-0.5" />
                          PDF with visual analysis
                        </span>
                      )}
                    </p>
                    {!isUploading && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2"
                        onClick={(e) => {
                          e.stopPropagation()
                          setFile(null)
                          setContent('')
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ) : (
                  <div>
                    <FileUp className="mx-auto mb-2 h-8 w-8 text-zinc-400" />
                    <p className="text-zinc-500 dark:text-zinc-400">
                      Click to select a file or drag and drop
                    </p>
                    <p className="text-xs text-zinc-400 mt-1">
                      PDF, TXT, MD - AI will extract insights
                    </p>
                  </div>
                )}
              </div>

              {/* Preview for non-PDF files */}
              {content && !file?.name.toLowerCase().endsWith('.pdf') && (
                <div className="space-y-2">
                  <Label className="text-xs">Preview</Label>
                  <div className="border rounded-lg p-3 max-h-32 overflow-y-auto bg-zinc-50 dark:bg-zinc-900">
                    <pre className="text-xs whitespace-pre-wrap font-mono">
                      {content.slice(0, 500)}
                      {content.length > 500 && '...'}
                    </pre>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Info message */}
          {!localError && (
            <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <Sparkles className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700 dark:text-blue-300">
                Analysis will start automatically after upload. You can watch progress in the Sources list.
              </div>
            </div>
          )}

          {/* Error Message */}
          {localError && (
            <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-700 dark:text-red-300">{localError}</div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isUploading || !title.trim() || (activeTab === 'paste' ? content.length < 50 : !file)}
            className="bg-solution hover:bg-solution/90"
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Upload Document
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
